import React, { useState, useEffect } from 'react'
import { getUserInfo } from '@/models/User';
import { updateCache } from '@/store/action';
import { showMessage, login } from '@/lib';
import { $api } from '@/queryClient';
import { useStore } from '@/store';
import config from '@/config';
import lang from '@/lib/lng';
import type { VerifyOTP, VerifyOTPRequest } from '@/@types/apiType';
import ModalUpdateEmail from './ModalUpdateEmail';
const lng: { [key: string]: string } = lang[config?.lng];

type Props = {
  closeModal: () => void
}

const VerifyEmail: React.FC<Props> = ({ closeModal }) => {
  const { mutate: otpTimeout } = $api.useMutation('post', '/api/user/get-otp-timeout');
  const { mutate: sendOTP } = $api.useMutation('post', '/api/user/send-otp');
  const { mutate: verifyOTP } = $api.useMutation('post', '/api/user/verify-otp');

  const [modalUpdateEmail, setModalUpdateEmail] = useState(false);

  // User info and store
  const userInfo = getUserInfo();
  const studentEmail = userInfo?.payload?.user?.student_email;
  const updateState = useStore((state) => state.updateState)
  const isCallingApi = useStore((state) => state.isCallingApi)

  const [otp, setOtp] = useState('');
  const [otpTime, setOtpTime] = useState(0);

  // Call otpTimeout when component mounts
  useEffect(() => {
    // Step 1: Event validation
    if (!userInfo?.payload?.event_started) {
      return;
    }

    // Step 2: User authentication
    if (!userInfo?.payload?.user?.id) {
      return;
    }

    // Step 3: Execute API call
    otpTimeout(
      { body: {} },
      {
        onSuccess: (data) => {
          if (data?.status === 'successful') {
            if (data?.payload?.otp_timeout && data?.payload?.otp_timeout > 0) {
              setOtpTime(data?.payload?.otp_timeout);
            } else {
              setOtpTime(0);
            }
          } else {
            const errCode: string = (data as any)?.error_code ?? 'defaultError';
            showMessage(
              lng[errCode] ? lng[errCode] : `${lng.defaultError} [${errCode}]`
            )
          }
        },
        onError: (error) => {
          console.error('OTP timeout API error:', error);
        },
      }
    );
  }, [userInfo?.payload?.event_started, userInfo?.payload?.user?.id, otpTimeout]);

  useEffect(() => {
    let timer: any;
    if (otpTime > 0) {
      timer = setInterval(() => {
        setOtpTime(prevTime => prevTime - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [otpTime]);

  const handleSendOTP = (e: React.SyntheticEvent) => {
    e.preventDefault();

    // Step 1: Event validation
    if (!userInfo?.payload?.event_started) {
      showMessage(config.eventMsg);
      return false;
    }

    // Step 2: User authentication
    if (!userInfo?.payload?.user?.id) {
      login();
      return false;
    }

    if (!studentEmail) {
      showMessage('Vui lòng cập nhật email sinh viên trước khi lấy mã OTP');
      return false;
    }

    if (otpTime > 0) {
      return false;
    }

    // Step 3: Prevent multiple calls
    if (isCallingApi) {
      return false;
    }

    // Step 4: Set loading state
    updateState('isCallingApi', true);

    // Step 5: Execute API call
    sendOTP(
      { body: {} },
      {
        onSuccess: (data) => {
          if ((data as any)?.status === 'successful') {
            // No cache update needed for sendOTP according to pattern
            showMessage('Mã OTP đã được gửi thành công. Bạn vui lòng kiểm tra email.').then(() => {
              updateState('isCallingApi', false);
              const serverTimeout = (data as any)?.payload?.otp_timeout;
              if (serverTimeout && serverTimeout > 0) {
                setOtpTime(serverTimeout);
              } else {
                // Query server for exact OTP timeout after sending
                otpTimeout(
                  { body: {} },
                  {
                    onSuccess: (resData) => {
                      if (resData?.status === 'successful' && resData?.payload?.otp_timeout) {
                        setOtpTime(resData.payload.otp_timeout);
                      }
                    },
                  }
                );
              }
            });
          } else {
            const errCode: string = (data as any)?.error_code ?? 'defaultError';
            showMessage(
              lng[errCode] ? lng[errCode] : `${lng.defaultError} [${errCode}]`
            ).then(() => {
              updateState('isCallingApi', false);
            });
          }
        },
        onError: () => {
          updateState('isCallingApi', false);
        },
      }
    );
  };

  const handleVerifyOTP = (e: React.SyntheticEvent) => {
    e.preventDefault();

    // Step 1: Event validation
    if (!userInfo?.payload?.event_started) {
      showMessage(config.eventMsg);
      return false;
    }

    // Step 2: User authentication
    if (!userInfo?.payload?.user?.id) {
      login();
      return false;
    }

    // Step 3: OTP validation
    if (!otp || otp.trim().length === 0) {
      showMessage('Vui lòng nhập mã OTP');
      return false;
    }

    // Step 4: Prevent multiple calls
    if (isCallingApi) {
      return false;
    }

    // Step 5: Set loading state
    updateState('isCallingApi', true);

    // Step 6: Prepare request data
    const requestData: VerifyOTPRequest = { code: otp.trim() };

    // Step 7: Execute API call
    verifyOTP(
      { body: requestData },
      {
        onSuccess: (data: VerifyOTP) => {
          if (data?.status === 'successful') {
            const oldCache = { ...userInfo };

            showMessage('Xác thực OTP thành công.').then(() => {
              updateCache(['get', '/api/user/get', {}], null, {
                ...oldCache,
                payload: {
                  ...oldCache?.payload,
                  user: {
                    ...oldCache?.payload?.user,
                    is_student: data?.payload?.user?.is_student
                  },
                },
              });
              updateState('isCallingApi', false);
              // Clear OTP input after successful verification
              setOtp('');
              closeModal();
            });
          } else {
            const errCode: string = (data as any)?.error_code ?? 'defaultError';
            showMessage(
              lng[errCode] ? lng[errCode] : `${lng.defaultError} [${errCode}]`
            ).then(() => {
              updateState('isCallingApi', false);
            });
          }
        },
        onError: () => {
          updateState('isCallingApi', false);
        },
      }
    );
  };

  return (
    <>
      <p>
        Lấy mã OTP để nhận mã xác thực. Hệ thống sẽ gửi mã về email
        <strong>
          {studentEmail || '(Chưa có email — vui lòng cập nhật email bên dưới)'}
        </strong>
        Mã này có hiệu lực trong 24 giờ.
      </p>
      <div className="verify-student__form__item">
        <input
          type="text"
          placeholder="Nhập mã OTP"
          value={otp}
          onChange={e => setOtp(e.target.value)}
        />
      </div>
      <div className="verify-student__form__actions">
        <a href="#"
          className="btn"
          onClick={e => handleVerifyOTP(e)}
        >
          {isCallingApi ? 'Xác nhận' : 'Xác nhận'}
        </a>
        {otpTime <= 0 ? (
          <a href="#"
            className={`btn ${!studentEmail ? 'btn-disabled' : ''}`}
            onClick={e => handleSendOTP(e)}
          >
            {isCallingApi ? 'Lấy mã OTP' : 'Lấy mã OTP'}
          </a>
        ) : (
          <a href="#"
            className="btn"
            onClick={e => handleSendOTP(e)}
          >
            {otpTime} giây
          </a>
        )}
        <a href="#"
          className="btn"
          onClick={e => {
            e.preventDefault();
            setModalUpdateEmail(true);
          }}
        >
          {isCallingApi ? 'Đổi email' : 'Đổi email'}
        </a>
      </div>
      {modalUpdateEmail && <ModalUpdateEmail modalStatus={modalUpdateEmail} closeModal={() => setModalUpdateEmail(false)} />}
    </>
  )
}

export default VerifyEmail